/**
 * Web themes, aligned with the Tapes mobile app (tapes-app/src/theme/colors.ts).
 *
 * The app ships one identity — VHS Violet on cool near-black — in a light and
 * a dark variant. The web app previously carried five unrelated palettes
 * (vhs/midnight/copper/tape/ghost) left over from before that identity
 * existed, which is why the site looked like a different product to the app.
 * Dark/Light below are the app's exact token values so the two read as one
 * thing; the extra palettes are gone rather than kept as dead options.
 */
export const THEMES = {
  dark: {
    id: 'dark', label: 'Dark',
    '--bg': '#0A0A0E', '--bg-alt': '#141318', '--bg-card': '#1B1A21', '--bg-elevated': '#221F2A', '--bg-tape': '#0F0E14',
    '--accent': '#A78BFA', '--accent-dim': '#6D28D9', '--accent-glow': 'rgba(167,139,250,0.14)',
    '--text': '#ECEAF2', '--text-dim': '#8F8D9B', '--text-muted': '#5A5866',
    '--border': '#26242E', '--border-bright': 'rgba(167,139,250,0.22)',
  },
  light: {
    id: 'light', label: 'Light',
    '--bg': '#FAFAFC', '--bg-alt': '#F1F0F6', '--bg-card': '#FFFFFF', '--bg-elevated': '#EAE8F2', '--bg-tape': '#F5F4F9',
    '--accent': '#7C3AED', '--accent-dim': '#5B21B6', '--accent-glow': 'rgba(124,58,237,0.10)',
    '--text': '#121116', '--text-dim': '#84828E', '--text-muted': '#A9A7B3',
    '--border': '#E4E2EC', '--border-bright': 'rgba(124,58,237,0.20)',
  },
};

const KEY = 'tapes_theme_v3';

// v2 stored ids that no longer exist (vhs/midnight/copper/tape/ghost). Map the
// old choice onto whichever of the two new themes matches its brightness so a
// returning user doesn't get dumped into an unexpected mode.
const LEGACY_MAP = {
  vhs: 'dark', midnight: 'dark', copper: 'dark',
  tape: 'light', ghost: 'light',
};

export const getTheme = () => {
  const stored = localStorage.getItem(KEY);
  if (stored && THEMES[stored]) return stored;

  const legacy = localStorage.getItem('tapes_theme_v2');
  if (legacy && LEGACY_MAP[legacy]) return LEGACY_MAP[legacy];

  // Match the app's default, which follows the OS.
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

export function applyTheme(id) {
  const t = THEMES[id] || THEMES.dark;
  const root = document.documentElement;
  Object.entries(t).forEach(([k, v]) => { if (k.startsWith('--')) root.style.setProperty(k, v); });
  localStorage.setItem(KEY, t.id);
}

export function initTheme() { applyTheme(getTheme()); }
